/**
 * MobileAccountMenu — avatar + menu compte du header mobile.
 *
 * @remarks
 * Le `MobileShell` ne rendait pas le user menu du Header desktop → le profil
 * était injoignable sur mobile. Cet avatar (initiales) ouvre un menu : Profil,
 * Paramètres, Aide, puis les pages que la bottom bar ne porte pas (liens
 * `bottom` du registry, SAAS-1200), Mentions légales, Déconnexion. Chaque
 * groupe est séparé par une barre. `Paramètres` vit ici plutôt que dans la
 * bottom bar (convention « compte »).
 *
 * « Aide & contact » n'ouvre pas de sous-menu latéral : à 375px, il n'a pas la
 * place à gauche du menu et sort de l'écran. Le menu bascule plutôt sur un
 * second écran (liens d'aide + « Retour »), dans le même cadre.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  LifeBuoy,
  LogOut,
  Scale,
  Settings as SettingsIcon,
  User as UserIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from '@/shared/ui';
import { featureRegistry, type AppContext, type Context } from '@/app/registry';
import { useAuthStore } from '@/app/stores/use-auth-store';
import { performLogout } from '@/lib/auth/logout';
import { HelpMenuItems } from './HelpMenuSubMenu';
import { MoreLinksMenuItems } from './MoreLinksMenuItems';
import { getMobileNavigationLinks } from './navigation-links';

/**
 * Mentions légales — pour l'instant on pointe vers la page ALT (SAAS-608).
 * À remplacer par une route interne quand la page dédiée existera.
 */
const MENTIONS_LEGALES_URL = 'https://www.alt.bzh/mentionslegales';

function getInitials(firstname?: string, lastname?: string, email?: string): string {
  const ini = `${firstname?.[0] ?? ''}${lastname?.[0] ?? ''}`.toUpperCase();
  return ini || (email?.[0]?.toUpperCase() ?? '');
}

interface Props {
  context: Context;
  appCtx: AppContext;
}

export function MobileAccountMenu({ context, appCtx }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'main' | 'help'>('main');
  const user = useAuthStore((s) => s.user);
  const initials = getInitials(user?.firstname, user?.lastname, user?.email);
  // Liens `bottom` du registry : la barre du bas n'a pas la place de les porter.
  const { bottomLinks: moreLinks } = getMobileNavigationLinks(
    featureRegistry.getAllNavigationLinks(appCtx),
  );
  // Quand la page courante est l'une de ces pages, l'avatar le signale : sans
  // cela, aucun onglet de la barre du bas n'est allumé et le repère se perd.
  const moreIsActive = moreLinks.some((l) => l.activeContexts.includes(context));

  const handleLogout = () => {
    void performLogout().finally(() => {
      void navigate('/login');
    });
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Rouvrir le menu repart toujours de l'écran principal.
        if (!next) setView('main');
      }}
    >
      <DropdownMenuTrigger
        aria-label="Mon compte"
        className={`bg-primary-bg-subtle text-primary data-[state=open]:ring-primary/40 inline-flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-shadow data-[state=open]:ring-2 ${moreIsActive ? 'ring-primary/40 ring-2' : ''}`}
      >
        {initials || <UserIcon className="size-4" aria-hidden />}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" collisionPadding={8}>
        {view === 'help' ? (
          <div className="w-72">
            <DropdownMenuItem
              onSelect={(e) => {
                // Garde le menu ouvert : on revient juste à l'écran principal.
                e.preventDefault();
                setView('main');
              }}
            >
              <Icon icon={ChevronLeft} size="md" />
              <span>Retour</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <HelpMenuItems />
          </div>
        ) : (
          <>
            <DropdownMenuItem asChild>
              <Link to="/profile">
                <Icon icon={UserIcon} size="md" />
                <span>Mon profil</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings">
                <Icon icon={SettingsIcon} size="md" />
                <span>Paramètres</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setView('help');
              }}
            >
              <Icon icon={LifeBuoy} size="md" />
              <span>Aide &amp; contact</span>
              <Icon icon={ChevronRight} size="md" className="text-fg-muted ml-auto" />
            </DropdownMenuItem>
            <MoreLinksMenuItems links={moreLinks} context={context} />
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a href={MENTIONS_LEGALES_URL} target="_blank" rel="noopener noreferrer">
                <Icon icon={Scale} size="md" />
                <span>Mentions légales</span>
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleLogout}>
              <Icon icon={LogOut} size="md" />
              <span>Déconnexion</span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
